/**
 * A count of what is waiting, and a list when you click it.
 *
 * **Reads the same `/alerts` payload AlertWatcher already polls**, under the
 * same query key, so the two share one cached response rather than putting two
 * requests on the same schedule. They are two views of one answer: the watcher
 * pushes the urgent ones into the corner, this one holds the tally.
 *
 * That overlap is deliberate but worth naming. The watcher shows a card and
 * goes away when dismissed; the bell keeps saying how much is outstanding, and
 * an alert dismissed from a card is still counted here until the underlying
 * record is dealt with. A bell that emptied on dismissal would be a second
 * notion of "handled" competing with the real one.
 *
 * **Read-only.** Acknowledging and completing are explicit actions elsewhere,
 * so opening this never marks anything as dealt with — the same rule the
 * endpoint's own docstring states.
 *
 * The count is honest about the owner's case: an owner is sent interviews and
 * nothing else by the server, so their bell is quieter than a recruiter's
 * rather than being wrong.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Bell, CalendarClock, FileText, PhoneCall, UserCheck } from 'lucide-react';
import { client } from '../api/client';
import { titleCaseName } from '../lib/titleCase';
import type { Alerts } from '../types';

/** One row, whatever list it came from. */
interface Row {
  key: string;
  icon: typeof Bell;
  text: string;
  detail: string | null;
  to: string;
}

export default function AlertBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  const { data } = useQuery<Alerts>({
    // The key AlertWatcher uses. One request, two readers.
    queryKey: ['alerts'],
    queryFn: async () => (await client.get('/api/v1/alerts')).data,
  });

  useEffect(() => {
    function onPointerDown(event: MouseEvent) {
      if (box.current && !box.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  const rows: Row[] = [
    // Prefixed keys, because the three lists carry their own `id` and an
    // interview and a followup could share one without the prefix.
    ...(data?.interviews ?? []).map((a) => ({
      key: `iv-${a.id}`,
      icon: CalendarClock,
      text: `Interview — ${titleCaseName(a.candidateName) ?? 'candidate'}`,
      detail: a.clientName,
      to: '/interviews',
    })),
    ...(data?.resumeDeadlines ?? []).map((a) => ({
      key: `rd-${a.id}`,
      icon: FileText,
      text: `Resume promised — ${titleCaseName(a.candidateName) ?? 'candidate'}`,
      detail: a.overdue ? 'Overdue' : null,
      to: '/candidates',
    })),
    ...(data?.joiningFollowups ?? []).map((a) => ({
      key: `jf-${a.id}`,
      icon: PhoneCall,
      text: `Joining check — ${titleCaseName(a.candidateName) ?? 'candidate'}`,
      detail: a.clientName,
      to: '/tracker',
    })),
    // Yet to Join, until somebody says they joined. Points at the candidate
    // list rather than the tracker: the status that clears this card is set
    // in that screen's edit dialog, and most of these have no placement for
    // the tracker to show.
    ...(data?.awaitingJoining ?? []).map((a) => ({
      key: `aj-${a.id}`,
      icon: UserCheck,
      text: `Yet to join — ${titleCaseName(a.candidateName) ?? 'candidate'}`,
      detail:
        a.daysUntil === null
          ? 'No joining date agreed'
          : a.daysUntil < 0
            ? `${Math.abs(a.daysUntil)} days overdue`
            : a.daysUntil === 0
              ? 'Joining today'
              : `In ${a.daysUntil} days`,
      to: '/candidates',
    })),
  ];

  const count = rows.length;

  return (
    <div ref={box} className="relative">
      <button
        type="button"
        className="btn-ghost relative transition-colors duration-300 ease-out"
        onClick={() => setOpen((o) => !o)}
        aria-label={count > 0 ? `${count} alerts` : 'No alerts'}
        title={count > 0 ? `${count} waiting` : 'Nothing waiting'}
      >
        <Bell className="h-4 w-4" />
        {count > 0 && (
          // 9+ rather than a three-digit badge overflowing the icon. The exact
          // number is one click away and the point of the badge is "there is
          // something", not "there are eleven".
          <span className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-red-600 px-1 text-[10px] font-semibold leading-none text-white">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full z-40 mt-1 w-80 overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-gray-200">
          <p className="border-b border-gray-100 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            Waiting on you
          </p>
          {count === 0 ? (
            <p className="px-3 py-4 text-sm text-gray-500">Nothing right now.</p>
          ) : (
            <ul className="max-h-80 overflow-y-auto p-1">
              {rows.map(({ key, icon: Icon, text, detail, to }) => (
                <li key={key}>
                  <button
                    type="button"
                    className="flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-gray-50"
                    onClick={() => {
                      setOpen(false);
                      navigate(to);
                    }}
                  >
                    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-indigo-500" />
                    <span className="min-w-0">
                      <span className="block truncate text-sm text-gray-900">
                        {text}
                      </span>
                      {detail && (
                        <span className="block truncate text-xs text-gray-500">
                          {detail}
                        </span>
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
